<template>
    <v-app>
        <v-container fluid>
            <v-row>
                <v-col cols="12">
                    <!-- Loading state while route is not ready -->
                    <div v-if="!employeeId" class="text-center pa-4">
                        <v-progress-circular indeterminate color="primary"></v-progress-circular>
                        <p class="mt-2">Loading employee details...</p>
                    </div>

                    <v-card v-else>
                        <v-card-title class="text-h5 pa-4">
                            <v-icon class="me-2">mdi-account-edit</v-icon>
                            Employee Details - {{ employeeName || 'Loading...' }}
                            <v-progress-circular v-if="loading" indeterminate size="20" class="ml-2"
                                color="primary"></v-progress-circular>
                        </v-card-title>

                        <v-card-text class="pa-0">
                            <v-row no-gutters>
                                <!-- Left Sidebar with Tabs -->
                                <v-col cols="12" md="3" class="border-e">
                                    <v-list density="compact" class="pa-0">
                                        <v-list-item v-for="tab in tabs" :key="tab.value" :value="tab.value"
                                            :active="activeTab === tab.value" @click="handleTabChange(tab.value)"
                                            class="tab-item" :class="{ 'tab-active': activeTab === tab.value }">
                                            <template v-slot:prepend>
                                                <v-icon :color="activeTab === tab.value ? 'primary' : 'grey'">
                                                    {{ tab.icon }}
                                                </v-icon>
                                            </template>
                                            <v-list-item-title class="text-caption">
                                                {{ tab.title }}
                                            </v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-col>

                                <!-- Right Content Area -->
                                <v-col cols="12" md="9" class="pa-4">
                                    <!-- Personal Information Tab Content -->
                                    <PersonalInformation v-if="activeTab === 'personal'"
                                        v-model:personal-info="personalInfo" :genders="genders"
                                        :blood-groups="bloodGroups" :religions="religions"
                                        :marital-statuses="maritalStatuses" :loading-states="loadingStates"
                                        ref="personalInfoRef" />

                                    <!-- General Information Tab Content -->
                                    <GeneralInformation v-if="activeTab === 'general'"
                                        v-model:official-info="officialInfo" v-model:bank-info="bankInfo"
                                        v-model:tax-info="taxInfo" v-model:driving-info="drivingInfo"
                                        :employee-types="employeeTypes" :departments="departments" :sections="sections"
                                        :designations="designations" 
                                        :banks="banks" 
                                        :loading-states="loadingStates"
                                        ref="generalInfoRef" />

                                    <!-- Salary Information Tab Content -->
                                    <SalaryInformation v-if="activeTab === 'salary'" v-model:salary-info="salaryInfo"
                                        :employee-id="employeeId" ref="salaryInfoRef" />

                                    <!-- Settings Information Tab Content -->
                                    <SettingsInformation v-if="activeTab === 'settings'"
                                        v-model:settings-info="settingsInfo" :employee-id="employeeId"
                                        ref="settingsInfoRef" />

                                    <!-- Educational Information Tab Content -->
                                    <EducationalInformationNew v-if="activeTab === 'educational'"
                                        :employee-id="employeeId" ref="educationalInfoRef" />

                                    <!-- Employee Address Tab Content -->
                                    <AddressInformation v-if="activeTab === 'address'"
                                        v-model:address-info="addressInfo" :employee-id="employeeId"
                                        ref="addressInfoRef" />

                                    <!-- Previous Employment Tab Content -->
                                    <PreviousEmployment v-if="activeTab === 'previous-employment'"
                                        v-model:previous-employment="previousEmployment" :employee-id="employeeId"
                                        :employee-joining-date="officialInfo.joining_date"
                                        ref="previousEmploymentRef" />

                                    <!-- Other Information Tab Content -->
                                    <OtherInformation v-if="activeTab === 'other'" v-model:other-info="otherInfo"
                                        :employee-id="employeeId" ref="otherInfoRef" />
                                </v-col>
                            </v-row>
                        </v-card-text>

                        <v-card-actions class="pa-4">
                            <v-spacer></v-spacer>
                            <v-btn color="primary" variant="outlined" @click="goBack" class="me-2">
                                <v-icon class="me-2">mdi-arrow-left</v-icon>
                                Back
                            </v-btn>
                            <v-btn color="primary" variant="flat" @click="saveEmployeeDetails" :loading="saving">
                                <v-icon class="me-2">mdi-content-save</v-icon>
                                Save Changes
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-col>
            </v-row>
        </v-container>
    </v-app>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { toast } from 'vue3-toastify';
import formattedDate from '@/plugins/dateFormatter'; // <-- add this import

// Import child components
import PersonalInformation from '@/components/module/hrm/employeeDetails/PersonalInformation.vue';
import GeneralInformation from '@/components/module/hrm/employeeDetails/GeneralInformation.vue';
import SalaryInformation from '@/components/module/hrm/employeeDetails/SalaryInformation.vue';
import SettingsInformation from '@/components/module/hrm/employeeDetails/SettingsInformation.vue';
import EducationalInformationNew from '@/components/module/hrm/employeeDetails/EducationalInformation.vue';
import AddressInformation from '@/components/module/hrm/employeeDetails/AddressInformation.vue';
import PreviousEmployment from '@/components/module/hrm/employeeDetails/PreviousEmployment.vue';
import OtherInformation from '@/components/module/hrm/employeeDetails/OtherInformation.vue';

export default {
    name: 'HrmEmployeeDetailsRefactored',
    components: {
        PersonalInformation,
        GeneralInformation,
        SalaryInformation,
        SettingsInformation,
        EducationalInformationNew,
        AddressInformation,
        PreviousEmployment,
        OtherInformation
    },
    setup() {
        const store = useStore();
        const route = useRoute();
        const router = useRouter();

        const activeTab = ref('personal');

        // Handle tab change and load specific data
        const handleTabChange = async (tab) => {
            activeTab.value = tab;

            // Load specific data when switching to certain tabs
            if (tab === 'settings') {
                await loadEmployeeSettingsInfo();
            }
        };

        // Computed property for safe employee ID access
        const employeeId = computed(() => {
            return route?.params?.id || null;
        });
        
        // Computed property for employee list
        const getHrmEmployeeList = computed(() => {
            return store.getters.getHrmEmployeeList || [];
        });
        
        const saving = ref(false);
        const employeeName = ref('Loading...');
        const loading = ref(false);

        // Dynamic dropdown data
        const departments = ref([]);
        const sections = ref([]);
        const designations = ref([]);
        const employeeTypes = ref([]);
        const bloodGroups = ref([]);
        const genders = ref([]);
        const religions = ref([]);
        const maritalStatuses = ref([]);
        const banks = ref([]);

        // Loading states for individual dropdowns
        const loadingStates = ref({
            departments: false,
            sections: false,
            designations: false,
            employeeTypes: false,
            bloodGroups: false,
            genders: false,
            religions: false,
            maritalStatuses: false,
            banks: false
        });

        // Tabs configuration
        const tabs = ref([
            { value: 'personal', title: 'Personal Information', icon: 'mdi-account' },
            { value: 'general', title: 'General Information', icon: 'mdi-information' },
            { value: 'salary', title: 'Salary Information', icon: 'mdi-cash' },
            { value: 'settings', title: 'Settings Information', icon: 'mdi-cog' },
            { value: 'educational', title: 'Educational Information', icon: 'mdi-school' },
            { value: 'address', title: 'Employee Address', icon: 'mdi-map-marker' },
            { value: 'previous-employment', title: 'Previous Employment', icon: 'mdi-briefcase-outline' },
            { value: 'other', title: 'Other Information', icon: 'mdi-account-details' }
        ]);

        // Form data for each tab
        const personalInfo = ref({
            name: '',
            name_bangla: '',
            father_name: '',
            mother_name: '',
            mobile: '',
            email: '',
            nid: '',
            passport_no: '',
            date_of_birth: '',
            nationality: '',
            height: '',
            weight: '',
            gender_id: '',
            blood_group_id: '',
            religion_id: '',
            marital_status_id: '',
            emergency_contact_person: '',
            emergency_contact_no: ''
        });

        const officialInfo = ref({
            code: '',
            status: '',
            employee_category: '',
            employee_type_id: '',
            department_id: '',
            section: '',
            designation_id: '',
            office_location: '',
            appointment_date: '',
            confirmation_date: '',
            joining_date: '',
            late_count: '',
            phone_office: '',
            phone_residence: '',
            hod_approve_employee_id: null,
            hr_approve_employee_id: null
        });

        const salaryInfo = ref({
            effect_date: '',
            package: '',
            gross_salary: '',
            basic_salary: '',
            house_rent: '',
            mobile: '',
            medical: '',
            conveyance: '',
            education: '',
            transport: '',
            technical: '',
            festival_allowance: '',
            tiffin: '',
            incentive: '',
            wash: '',
            others: '',
            tax_deducted: '',
            car_use: '',
            pf_provident_fund: '',
            gas_bill: '',
            electricity_bill: '',
            telephone_bill: '',
            insurance: '',
            others_deducted: ''
        });

        const settingsInfo = ref({
            ot_applicable: '',
            daily_ot_type: '',
            daily_ot_rate: '',
            daily_ot_pay_on: '',
            daily_ot_amount: '',
            hourly_ot_type: '',
            hourly_ot_rate: '',
            hourly_ot_rate_percentage: '',
            hourly_ot_pay_on: '',
            hourly_ot_amount: ''
        });

        // Educational info is now handled by the EducationalInformationNew component

        const addressInfo = ref({
            permanent_address: '',
            present_address: '',
            city: '',
            district: '',
            postal_code: ''
        });

        const previousEmployment = ref({
            company_name: '',
            position: '',
            start_date: '',
            end_date: '',
            responsibilities: ''
        });

        const otherInfo = ref({
            skills: '',
            languages: '',
            hobbies: '',
            references: '',
            notes: '',
            achievements: '',
            special_instructions: '',
            medical_conditions: '',
            allergies: '',
            emergency_medical_info: '',
            extra_curricular_activities: '',
            extra_curricular_remarks: '',
            area_of_specialization: '',
            specialization_remarks: '',
            language_proficiencies: []
        });

        const bankInfo = ref({
            bank_id: '',
            account_number: ''
        });

        const taxInfo = ref({
            tin_number: '',
            tax_circle: '',
            tax_zone: ''
        });

        const drivingInfo = ref({
            license_number: '',
            renewal_from: '',
            renewal_to: ''
        });

        // Static options for dropdowns
        const yearOptions = ref([]);
        const boardOptions = ref([]);
        const examinationOptions = ref([]);
        const resultOptions = ref([]);

        // Component refs
        const personalInfoRef = ref(null);
        const generalInfoRef = ref(null);
        const salaryInfoRef = ref(null);
        const settingsInfoRef = ref(null);
        const educationalInfoRef = ref(null);
        const addressInfoRef = ref(null);
        const previousEmploymentRef = ref(null);
        const otherInfoRef = ref(null);

        // Initialize static options
        const initializeStaticOptions = () => {
            // Generate year options (last 50 years)
            const currentYear = new Date().getFullYear();
            yearOptions.value = Array.from({ length: 50 }, (_, i) => ({
                text: (currentYear - i).toString(),
                value: (currentYear - i).toString()
            }));

            // Board options
            boardOptions.value = [
                { text: 'Dhaka', value: 'dhaka' },
                { text: 'Chittagong', value: 'chittagong' },
                { text: 'Rajshahi', value: 'rajshahi' },
                { text: 'Sylhet', value: 'sylhet' },
                { text: 'Barisal', value: 'barisal' },
                { text: 'Comilla', value: 'comilla' },
                { text: 'Jessore', value: 'jessore' },
                { text: 'Dinajpur', value: 'dinajpur' }
            ];

            // Examination options
            examinationOptions.value = [
                { text: 'SSC', value: 'ssc' },
                { text: 'HSC', value: 'hsc' },
                { text: 'Bachelor', value: 'bachelor' },
                { text: 'Masters', value: 'masters' },
                { text: 'PhD', value: 'phd' }
            ];

            // Result options
            resultOptions.value = [
                { text: 'First Division', value: 'first' },
                { text: 'Second Division', value: 'second' },
                { text: 'Third Division', value: 'third' },
                { text: 'GPA', value: 'gpa' }
            ];
        };

        // Load settings information data
        const loadEmployeeSettingsInfo = async () => {
            try {
                const response = await store.dispatch('settingsInfo/getSettingsInfoByEmployee', route.params.id);

                if (response.success && response.data) {
                    const settingsData = response.data;
                    settingsInfo.value = {
                        ot_applicable: settingsData.ot_applicable || '',
                        daily_ot_type: settingsData.daily_ot_type || '',
                        daily_ot_rate: settingsData.daily_ot_rate || '',
                        daily_ot_pay_on: settingsData.daily_ot_pay_on || '',
                        daily_ot_amount: settingsData.daily_ot_amount || '',
                        hourly_ot_type: settingsData.hourly_ot_type || '',
                        hourly_ot_rate: settingsData.hourly_ot_rate || '',
                        hourly_ot_rate_percentage: settingsData.hourly_ot_rate_percentage || '',
                        hourly_ot_pay_on: settingsData.hourly_ot_pay_on || '',
                        hourly_ot_amount: settingsData.hourly_ot_amount || ''
                    };
                }
            } catch (error) {
                console.error('Error loading settings info:', error);
                // Don't show error toast for missing data, it's normal for new employees
            }
        };

        // Load employee data
        const loadEmployeeData = async () => {
            try {
                loading.value = true;
                const employeeId = route.params.id;

                if (employeeId) {
                    console.log('Loading employee data for ID:', employeeId);

                    // Load employee data from API
                    const response = await store.dispatch('getEmployeeDetails', employeeId);

                    if (response.data.success) {
                        const employee = response.data.data;

                        // Set employee name
                        employeeName.value = employee.name || 'Unknown Employee';

                        // Populate personal information
                        if (employee.personal_information) {
                            personalInfo.value = {
                                name: employee.personal_information.name || '',
                                name_bangla: employee.personal_information.name_bangla || '',
                                father_name: employee.personal_information.father_name || '',
                                mother_name: employee.personal_information.mother_name || '',
                                mobile: employee.personal_information.mobile || '',
                                email: employee.personal_information.email || '',
                                nid: employee.personal_information.nid || '',
                                passport_no: employee.personal_information.passport_no || '',
                                date_of_birth: employee.personal_information.date_of_birth || '',
                                nationality: employee.personal_information.nationality || '',
                                height: employee.personal_information.height || '',
                                weight: employee.personal_information.weight || '',
                                gender_id: employee.personal_information.gender_id || '',
                                blood_group_id: employee.personal_information.blood_group_id || '',
                                religion_id: employee.personal_information.religion_id || '',
                                marital_status_id: employee.personal_information.marital_status_id || '',
                                emergency_contact_person: employee.personal_information.emergency_contact_person || '',
                                emergency_contact_no: employee.personal_information.emergency_contact_no || ''
                            };
                        } else {
                            // Fallback to main employee table if personal_information doesn't exist
                            personalInfo.value = {
                                name: employee.name || '',
                                name_bangla: employee.name_bangla || '',
                                father_name: employee.father_name || '',
                                mother_name: employee.mother_name || '',
                                mobile: employee.mobile || '',
                                email: employee.email || '',
                                nid: employee.nid || '',
                                passport_no: employee.passport_no || '',
                                date_of_birth: employee.date_of_birth || '',
                                nationality: employee.nationality || '',
                                height: employee.height || '',
                                weight: employee.weight || '',
                                gender_id: employee.gender_id || '',
                                blood_group_id: employee.blood_group_id || '',
                                religion_id: employee.religion_id || '',
                                marital_status_id: employee.marital_status_id || '',
                                emergency_contact_person: employee.emergency_contact_person || '',
                                emergency_contact_no: employee.emergency_contact_no || ''
                            };
                        }

                        // Populate official information
                        // if (employee.official_information) {
                        //     officialInfo.value = {
                        //         code: employee.official_information.code || '',
                        //         status: employee.official_information.employee_status || '',
                        //         employee_category: employee.official_information.employee_category || '',
                        //         employee_type_id: employee.official_information.employee_type_id || '',
                        //         department_id: employee.official_information.department_id || '',
                        //         section: employee.official_information.section_id || '',
                        //         designation_id: employee.official_information.designation_id || '',
                        //         office_location: employee.official_information.office_location || '',
                        //         appointment_date: employee.official_information.appointment_date || '',
                        //         confirmation_date: employee.official_information.confirmation_date || '',
                        //         joining_date: employee.official_information.joining_date || '',
                        //         late_count: employee.official_information.late_count || '',
                        //         phone_office: employee.official_information.phone_office || '',
                        //         phone_residence: employee.official_information.phone_residence || ''
                        //     };
                        // } else {
                            // Fallback to main employee table if official_information doesn't exist
                            // officialInfo.value = {
                            //     code: employee.code || '',
                            //     status: employee.status || '',
                            //     employee_category: '',
                            //     employee_type_id: employee.employee_type_id || '',
                            //     department_id: employee.department_id || '',
                            //     section: employee.section_id || '',
                            //     designation_id: employee.designation_id || '',
                            //     office_location: '',
                            //     appointment_date: '',
                            //     confirmation_date: '',
                            //     joining_date: employee.joining_date || '',
                            //     late_count: '',
                            //     phone_office: employee.phone_office || '',
                            //     phone_residence: employee.phone_residence || ''
                            // };
                        // }

                        // // Populate settings information (only overtime fields)
                        // if (employee.settings_information) {
                        //     settingsInfo.value = {
                        //         ot_applicable: employee.settings_information.ot_applicable || '',
                        //         daily_ot_type: employee.settings_information.daily_ot_type || '',
                        //         daily_ot_rate: employee.settings_information.daily_ot_rate || '',
                        //         daily_ot_pay_on: employee.settings_information.daily_ot_pay_on || '',
                        //         daily_ot_amount: employee.settings_information.daily_ot_amount || '',
                        //         hourly_ot_type: employee.settings_information.hourly_ot_type || '',
                        //         hourly_ot_rate: employee.settings_information.hourly_ot_rate || '',
                        //         hourly_ot_rate_percentage: employee.settings_information.hourly_ot_rate_percentage || '',
                        //         hourly_ot_pay_on: employee.settings_information.hourly_ot_pay_on || '',
                        //         hourly_ot_amount: employee.settings_information.hourly_ot_amount || ''
                        //     };
                        // }

                        // Populate salary information
                        // if (employee.salary_information) {
                        //     salaryInfo.value = {
                        //         effect_date: employee.salary_information.effect_date || '',
                        //         package: employee.salary_information.package || '',
                        //         gross_salary: employee.salary_information.gross_salary || '',
                        //         basic_salary: employee.salary_information.basic_salary || '',
                        //         house_rent: employee.salary_information.house_rent || '',
                        //         mobile: employee.salary_information.mobile_allowance || '',
                        //         medical: employee.salary_information.medical_allowance || '',
                        //         education: employee.salary_information.education_allowance || '',
                        //         transport: employee.salary_information.transport_allowance || '',
                        //         technical: employee.salary_information.technical_allowance || '',
                        //         festival_allowance: employee.salary_information.festival_allowance || '',
                        //         tiffin: employee.salary_information.tiffin_allowance || '',
                        //         incentive: employee.salary_information.incentive || '',
                        //         wash: employee.salary_information.wash_allowance || '',
                        //         others: employee.salary_information.others_allowance || '',
                        //         tax_deducted: employee.salary_information.tax_deducted || '',
                        //         car_use: employee.salary_information.car_use_charge || '',
                        //         pf_provident_fund: employee.salary_information.pf_provident_fund || '',
                        //         gas_bill: employee.salary_information.gas_bill || '',
                        //         electricity_bill: employee.salary_information.electricity_bill || '',
                        //         telephone_bill: employee.salary_information.telephone_bill || '',
                        //         insurance: employee.salary_information.insurance || '',
                        //         others_deducted: employee.salary_information.others_deducted || ''
                        //     };
                        // }

                        // Educational information is now loaded by the EducationalInformationNew component

                        // Populate previous employment
                        // if (employee.previous_employment && employee.previous_employment.length > 0) {
                        //     previousEmployment.value = employee.previous_employment.map(emp => ({
                        //         company_name: emp.company_name || '',
                        //         designation: emp.designation || '',
                        //         joining_date: emp.joining_date || '',
                        //         leaving_date: emp.leaving_date || '',
                        //         last_salary: emp.last_salary || '',
                        //         job_responsibilities: emp.job_responsibilities || '',
                        //         reason_for_leaving: emp.reason_for_leaving || '',
                        //         supervisor_name: emp.supervisor_name || '',
                        //         supervisor_contact: emp.supervisor_contact || ''
                        //     }));
                        // }

                        // Populate other information
                        // if (employee.other_information) {
                        //     otherInfo.value = {
                        //         skills: employee.other_information.skills || '',
                        //         languages: employee.other_information.languages || '',
                        //         hobbies: employee.other_information.hobbies || '',
                        //         achievements: employee.other_information.achievements || '',
                        //         references: employee.other_information.references || '',
                        //         notes: employee.other_information.notes || '',
                        //         special_instructions: employee.other_information.special_instructions || '',
                        //         medical_conditions: employee.other_information.medical_conditions || '',
                        //         allergies: employee.other_information.allergies || '',
                        //         emergency_medical_info: employee.other_information.emergency_medical_info || '',
                        //         extra_curricular_activities: employee.other_information.extra_curricular_activities || '',
                        //         extra_curricular_remarks: employee.other_information.extra_curricular_remarks || '',
                        //         area_of_specialization: employee.other_information.area_of_specialization || '',
                        //         specialization_remarks: employee.other_information.specialization_remarks || '',
                        //         language_proficiencies: employee.other_information.language_proficiencies || []
                        //     };
                        // }

                        // Populate address information
                        // if (employee.address_information) {
                        //     addressInfo.value = {
                        //         present_division: employee.address_information.present_division || '',
                        //         present_district: employee.address_information.present_district || '',
                        //         present_thana_upazilla: employee.address_information.present_thana_upazilla || '',
                        //         present_address: employee.address_information.present_address || '',
                        //         permanent_division: employee.address_information.permanent_division || '',
                        //         permanent_district: employee.address_information.permanent_district || '',
                        //         permanent_thana_upazilla: employee.address_information.permanent_thana_upazilla || '',
                        //         permanent_address: employee.address_information.permanent_address || ''
                        //     };
                        // } else {
                        //     // Fallback to main employee table if address_information doesn't exist
                        //     addressInfo.value = {
                        //         present_division: employee.present_division || '',
                        //         present_district: employee.present_district || '',
                        //         present_thana_upazilla: employee.present_thana_upazilla || '',
                        //         present_address: employee.present_address || '',
                        //         permanent_division: employee.permanent_division || '',
                        //         permanent_district: employee.permanent_district || '',
                        //         permanent_thana_upazilla: employee.permanent_thana_upazilla || '',
                        //         permanent_address: employee.permanent_address || ''
                        //     };
                        // }

                        toast.success('Employee data loaded successfully');
                    } else {
                        toast.error('Failed to load employee data');
                    }
                }
            } catch (error) {
                console.error('Error loading employee data:', error);
                toast.error('Failed to load employee data: ' + (error.response?.data?.message || error.message));
            } finally {
                loading.value = false;
            }
        };

        // Load employee general information data
        const loadEmployeeGeneralInfo = async () => {
            try {
                const employeeId = route.params.id;

                if (employeeId) {
                    // Load general info data from API
                    const response = await store.dispatch('getEmployeeGeneralInfo', employeeId);

                    if (response.data.success) {
                        const data = response.data.data;

                        officialInfo.value = {
                            code: data.employee.code || '',
                            status: data.employee.status || '',
                            employee_type_id: data.employee.employee_type_id || '',
                            department_id: data.employee.department_id || '',
                            section: data.employee.section_id || '',
                            designation_id: data.employee.designation_id || '',
                            phone_office: data.employee.phone_office || '',
                            phone_residence: data.employee.phone_residence || '',
                            hod_approve_employee_id: data.employee.hod_approve_employee_id || null,
                            hr_approve_employee_id: data.employee.hr_approve_employee_id || null
                        };
                        
                        if (data.general_info) {
                            // Format dates for frontend
                            const formatDate = (dateString) => {
                                if (!dateString) return '';
                                const date = new Date(dateString);
                                return date.toISOString().split('T')[0]; // YYYY-MM-DD format
                            };

                            officialInfo.value = {
                                code: data.employee.code || '',
                                status: data.employee.status || '',
                                employee_type_id: data.employee.employee_type_id || '',
                                department_id: data.employee.department_id || '',
                                section: data.employee.section_id || '',
                                designation_id: data.employee.designation_id || '',
                                phone_office: data.employee.phone_office || '',
                                phone_residence: data.employee.phone_residence || '',
                                employee_category: data.general_info.employee_category || '',
                                office_location: data.general_info.office_location || '',
                                appointment_date: formatDate(data.general_info.appointment_date),
                                confirmation_date: formatDate(data.general_info.confirmation_date),
                                joining_date: formatDate(data.general_info.joining_date),
                                late_count: data.general_info.late_count || '',
                                hod_approve_employee_id: data.employee.hod_approve_employee_id || null,
                                hr_approve_employee_id: data.employee.hr_approve_employee_id || null
                            };
                        }

                        // Populate bank information
                        if (data.bank_info) {
                            bankInfo.value = {
                                bank_id: data.bank_info.bank.id,
                                account_number: data.bank_info.account_number || ''
                            };
                        }

                        // Populate tax information
                        if (data.tax_info) {
                            taxInfo.value = {
                                tin_number: data.tax_info.tin_number || '',
                                tax_circle: data.tax_info.tax_circle || '',
                                tax_zone: data.tax_info.tax_zone || ''
                            };
                        }

                        // Populate driving information
                        if (data.driving_info) {
                            // Format dates for frontend
                            const formatDate = (dateString) => {
                                if (!dateString) return '';
                                const date = new Date(dateString);
                                return date.toISOString().split('T')[0]; // YYYY-MM-DD format
                            };

                            drivingInfo.value = {
                                license_number: data.driving_info.license_number || '',
                                renewal_from: formatDate(data.driving_info.renewal_from),
                                renewal_to: formatDate(data.driving_info.renewal_to)
                            };
                        }
                    }
                }
            } catch (error) {
                console.error('Error loading employee general info:', error);
                // Don't show error toast as this is optional data
            }
        };

        // Load employee salary information data
        const loadEmployeeSalaryInfo = async () => {
            try {
                const currentEmployeeId = employeeId.value;

                if (!currentEmployeeId) {
                    return;
                }

                if (currentEmployeeId) {
                    // Load salary info data from API
                    const response = await store.dispatch('getSalaryInfoByEmployee', currentEmployeeId);

                    if (response && response.data && response.data.success && response.data.data && response.data.data.length > 0) {
                        // Get the latest salary information
                        const latestSalaryInfo = response.data.data[0];

                        // Format date for frontend
                        const formatDate = (dateString) => {
                            if (!dateString) return '';
                            const date = new Date(dateString);
                            return date.toISOString().split('T')[0]; // YYYY-MM-DD format
                        };

                        const formattedSalaryInfo = {
                            salary_info_id: latestSalaryInfo.id, // Include the ID for updates
                            effect_date: formatDate(latestSalaryInfo.effect_date),
                            package: latestSalaryInfo.package || '',
                            gross_salary: latestSalaryInfo.gross_salary || 0,
                            basic_salary: latestSalaryInfo.basic_salary || 0,
                            house_rent: latestSalaryInfo.house_rent || 0,
                            mobile: latestSalaryInfo.mobile || 0,
                            medical: latestSalaryInfo.medical || 0,
                            conveyance: latestSalaryInfo.conveyance || 0,
                            education: latestSalaryInfo.education || 0,
                            transport: latestSalaryInfo.transport || 0,
                            technical: latestSalaryInfo.technical || 0,
                            festival_allowance: latestSalaryInfo.festival_allowance || 0,
                            tiffin: latestSalaryInfo.tiffin || 0,
                            incentive: latestSalaryInfo.incentive || 0,
                            wash: latestSalaryInfo.wash || 0,
                            others: latestSalaryInfo.others || 0,
                            tax_deducted: latestSalaryInfo.tax_deducted || 0,
                            car_use: latestSalaryInfo.car_use || 0,
                            pf_provident_fund: latestSalaryInfo.pf_provident_fund || 0,
                            gas_bill: latestSalaryInfo.gas_bill || 0,
                            electricity_bill: latestSalaryInfo.electricity_bill || 0,
                            telephone_bill: latestSalaryInfo.telephone_bill || 0,
                            insurance: latestSalaryInfo.insurance || 0,
                            others_deducted: latestSalaryInfo.others_deducted || ''
                        };

                        salaryInfo.value = formattedSalaryInfo;
                    } else {
                        // Initialize with empty values if no data found
                        salaryInfo.value = {
                            effect_date: '',
                            package: '',
                            gross_salary: 0,
                            basic_salary: 0,
                            house_rent: 0,
                            mobile: 0,
                            medical: 0,
                            conveyance: 0,
                            education: 0,
                            transport: 0,
                            technical: 0,
                            festival_allowance: 0,
                            tiffin: 0,
                            incentive: 0,
                            wash: 0,
                            others: 0,
                            tax_deducted: 0,
                            car_use: 0,
                            pf_provident_fund: 0,
                            gas_bill: 0,
                            electricity_bill: 0,
                            telephone_bill: 0,
                            insurance: 0,
                            others_deducted: ''
                        };
                    }
                }
            } catch (error) {
                console.error('Error loading employee salary info:', error);
                // Don't show error toast as this is optional data
            }
        };

        // Load dropdown data
        const loadDropdownData = async () => {
            try {
                // Load departments
                loadingStates.value.departments = true;
                await store.dispatch('getHrmDepartmentList');
                departments.value = store.getters.getHrmDepartmentList.map(dept => ({
                    text: dept.name,
                    value: dept.id
                }));
                loadingStates.value.departments = false;

                // Load sections
                loadingStates.value.sections = true;
                await store.dispatch('getHrmSectionList');
                sections.value = store.getters.getHrmSectionList.map(section => ({
                    text: section.name,
                    value: section.id
                }));
                loadingStates.value.sections = false;

                // Load designations
                loadingStates.value.designations = true;
                await store.dispatch('getHrmDesignationList');
                designations.value = store.getters.getHrmDesignationList.map(designation => ({
                    text: designation.name,
                    value: designation.id
                }));
                loadingStates.value.designations = false;

                // Load employee types
                loadingStates.value.employeeTypes = true;
                await store.dispatch('getHrmEmployeeTypeList');
                employeeTypes.value = store.getters.getHrmEmployeeTypeList.map(type => ({
                    text: type.name,
                    value: type.id
                }));
                loadingStates.value.employeeTypes = false;

                // Load blood groups
                await store.dispatch('getHrmBloodGroupList');
                bloodGroups.value = store.getters.getHrmBloodGroupList.map(blood => ({
                    text: blood.name,
                    value: blood.id
                }));

                // Load genders
                await store.dispatch('getHrmGenderList');
                genders.value = store.getters.getHrmGenderList.map(gender => ({
                    text: gender.name,
                    value: gender.id
                }));

                // Load religions
                await store.dispatch('getHrmReligionList');
                religions.value = store.getters.getHrmReligionList.map(religion => ({
                    text: religion.name,
                    value: religion.id
                }));

                // Load marital statuses
                await store.dispatch('getHrmMaritalStatusList');
                maritalStatuses.value = store.getters.getHrmMaritalStatusList.map(status => ({
                    text: status.name,
                    value: status.id
                }));

                // Load Banks
                loadingStates.value.banks = true;
                await store.dispatch('getHrmEmployeeBankSetupList').then(res => {
                    loadingStates.value.banks = false;
                }).finally(()=> loadingStates.value.banks = false);
                banks.value = store.getters.getHrmEmployeeBankSetupList.map(bank => ({
                    text: bank.name,
                    value: bank.id
                }));


            } catch (error) {
                console.error('Error loading dropdown data:', error);
                toast.error('Failed to load dropdown data');
            }
        };

        // Save employee details
        const saveEmployeeDetails = async () => {
            try {
                saving.value = true;

                // Validate current tab form
                const currentTabRef = getCurrentTabRef();
                if (currentTabRef && currentTabRef.validate) {
                    const isValid = await currentTabRef.validate();
                    if (!isValid) {
                        toast.error('Please fix validation errors before saving');
                        return;
                    }
                }
                
                if (activeTab.value === 'personal') {
                    const personalInfoSaved = await saveEmployeePersonalInfo(true); // Don't show individual notification
                }
                // Also save general information if we're on the official tab
                else if (activeTab.value === 'general') {
                    const generalInfoSaved = await saveEmployeeGeneralInfo(true); // Don't show individual notification
                }
                // Also save salary information if we're on the salary tab
                else if (activeTab.value === 'salary') {
                    const salaryInfoSaved = await saveEmployeeSalaryInfo(false); // Don't show individual notification
                    if (salaryInfoSaved) {
                        toast.success('Employee details and salary information saved successfully');
                    } else {
                        toast.success('Employee details saved successfully, but salary information failed to save');
                    }
                }
                // Also save settings information if we're on the settings tab
                else if (activeTab.value === 'settings') {
                    if (settingsInfoRef.value && settingsInfoRef.value.saveSettingsInfo) {
                        try {
                            await settingsInfoRef.value.saveSettingsInfo();
                            toast.success('Employee details and settings information saved successfully');
                        } catch (error) {
                            console.error('Error saving settings info:', error);
                            toast.success('Employee details saved successfully, but settings information failed to save');
                        }
                    } else {
                        toast.success('Employee details saved successfully');
                    }
                }
                // Also save educational information if we're on the educational tab
                else if (activeTab.value === 'educational') {
                    if (educationalInfoRef.value && educationalInfoRef.value.saveAllEducationalInfo) {
                        try {
                            await educationalInfoRef.value.saveAllEducationalInfo();
                            toast.success('Employee details and educational information saved successfully');
                        } catch (error) {
                            console.error('Error saving educational info:', error);
                            toast.success('Employee details saved successfully, but educational information failed to save');
                        }
                    } else {
                        toast.success('Employee details saved successfully');
                    }
                }
                // Also save address information if we're on the address tab
                else if (activeTab.value === 'address') {
                    if (addressInfoRef.value && addressInfoRef.value.saveAddressInfo) {
                        try {
                            await addressInfoRef.value.saveAddressInfo();
                            toast.success('Employee details and address information saved successfully');
                        } catch (error) {
                            console.error('Error saving address info:', error);
                            toast.success('Employee details saved successfully, but address information failed to save');
                        }
                    } else {
                        toast.success('Employee details saved successfully');
                    }
                }
                // Also save previous employment information if we're on the previous-employment tab
                else if (activeTab.value === 'previous-employment') {
                    if (previousEmploymentRef.value && previousEmploymentRef.value.saveAllPreviousEmploymentInfo) {
                        try {
                            await previousEmploymentRef.value.saveAllPreviousEmploymentInfo();
                            toast.success('Employee details and previous employment information saved successfully');
                        } catch (error) {
                            console.error('Error saving previous employment info:', error);
                            toast.success('Employee details saved successfully, but previous employment information failed to save');
                        }
                    } else {
                        toast.success('Employee details saved successfully');
                    }
                }
                // Also save other information if we're on the other tab
                else if (activeTab.value === 'other') {
                    if (otherInfoRef.value && otherInfoRef.value.saveOtherInfo) {
                        try {
                            await otherInfoRef.value.saveOtherInfo();
                            toast.success('Employee details and other information saved successfully');
                        } catch (error) {
                            console.error('Error saving other info:', error);
                            toast.success('Employee details saved successfully, but other information failed to save');
                        }
                    } else {
                        toast.success('Employee details saved successfully');
                    }
                }
                else {
                    toast.success('Employee details saved successfully');
                }
            } catch (error) {
                console.error('Error saving employee data:', error);
                toast.error('Failed to save employee details: ' + (error.response?.data?.message || error.message));
            } finally {
                saving.value = false;
            }
        };

        const saveEmployeePersonalInfo = async (showNotification = true) => {
            try {
                const employeeId = route.params.id;

                if (employeeId) {
                    const personalInfoData = {
                        employee_id: route.params.id,
                        // Personal Information
                        name: personalInfo.value.name,
                        name_bangla: personalInfo.value.name_bangla,
                        father_name: personalInfo.value.father_name,
                        mother_name: personalInfo.value.mother_name,
                        mobile: personalInfo.value.mobile,
                        email: personalInfo.value.email,
                        nid: personalInfo.value.nid,
                        passport_no: personalInfo.value.passport_no,
                        date_of_birth: personalInfo.value.date_of_birth,
                        nationality: personalInfo.value.nationality,
                        height: personalInfo.value.height,
                        weight: personalInfo.value.weight,

                        // Official Information
                        code: officialInfo.value.code,
                        status: officialInfo.value.status,
                        joining_date: formattedDate(officialInfo.value.joining_date),
                        phone_office: formattedDate(officialInfo.value.phone_office),
                        phone_residence: formattedDate(officialInfo.value.phone_residence),
                        department_id: officialInfo.value.department_id,
                        designation_id: officialInfo.value.designation_id,
                        section_id: officialInfo.value.section,
                        employee_type_id: officialInfo.value.employee_type_id,
                        hod_approve_employee_id: officialInfo.value.hod_approve_employee_id,
                        hr_approve_employee_id: officialInfo.value.hr_approve_employee_id,

                        // Personal/Settings Information
                        gender_id: personalInfo.value.gender_id,
                        blood_group_id: personalInfo.value.blood_group_id,
                        religion_id: personalInfo.value.religion_id,
                        marital_status_id: personalInfo.value.marital_status_id,
                        emergency_contact_person: personalInfo.value.emergency_contact_person,
                        emergency_contact_no: personalInfo.value.emergency_contact_no,

                        // Overtime Settings
                        ot_applicable: settingsInfo.value.ot_applicable,
                        daily_ot_type: settingsInfo.value.daily_ot_type,
                        daily_ot_rate: settingsInfo.value.daily_ot_rate,
                        daily_ot_pay_on: settingsInfo.value.daily_ot_pay_on,
                        daily_ot_amount: settingsInfo.value.daily_ot_amount,
                        hourly_ot_type: settingsInfo.value.hourly_ot_type,
                        hourly_ot_rate: settingsInfo.value.hourly_ot_rate,
                        hourly_ot_rate_percentage: settingsInfo.value.hourly_ot_rate_percentage,
                        hourly_ot_pay_on: settingsInfo.value.hourly_ot_pay_on,
                        hourly_ot_amount: settingsInfo.value.hourly_ot_amount,
                        permanent_address: addressInfo.value.permanent_address
                    };
                    
                    const response = await store.dispatch('saveUpdateEmployeeSetup', personalInfoData);
                    if (response.data.success) {
                        if (showNotification) {
                            toast.success('Personal information saved successfully');
                        }
                        return true;
                    } else {
                        if (showNotification) {
                            toast.error('Failed to save personal information');
                        }
                        return false;
                    }
                }
            } catch (error) {
                const backendErrors = error.response?.data?.errors;
                if (backendErrors && typeof backendErrors === 'object') {
                    Object.values(backendErrors)
                        .flat()
                        .forEach(msg => toast.error(msg));
                } else {
                    const msg = error.response?.data?.message || 'Failed to save personal information';
                    toast.error(msg);
                }
                return false;      
            }
        }

        const saveEmployeeGeneralInfo = async (showNotification = true) => {
            try {
                const employeeId = route.params.id;

                if (employeeId) {
                    // Prepare general info data
                    const generalInfoData = {
                        employee_id: employeeId,
                        general_info: {
                            employee_category: officialInfo.value.employee_category,
                            office_location: officialInfo.value.office_location,
                            appointment_date: formattedDate(officialInfo.value.appointment_date),
                            confirmation_date: formattedDate(officialInfo.value.confirmation_date),
                            joining_date: formattedDate(officialInfo.value.joining_date),
                            late_count: officialInfo.value.late_count,
                            phone_office: officialInfo.value.phone_office,
                            phone_residence: officialInfo.value.phone_residence,
                            hod_approve_employee_id: officialInfo.value.hod_approve_employee_id,
                            hr_approve_employee_id: officialInfo.value.hr_approve_employee_id
                        },
                        bank_info: {
                            bank_id: bankInfo.value.bank_id,
                            account_number: bankInfo.value.account_number
                        },
                        tax_info: {
                            tin_number: taxInfo.value.tin_number,
                            tax_circle: taxInfo.value.tax_circle,
                            tax_zone: taxInfo.value.tax_zone
                        },
                        driving_info: {
                            license_number: drivingInfo.value.license_number,
                            renewal_from: drivingInfo.value.renewal_from,
                            renewal_to: drivingInfo.value.renewal_to
                        }
                    };
                    // Call the general info API
                    const response = await store.dispatch('saveEmployeeGeneralInfo', generalInfoData);

                    if (response.data.success) {
                        if (showNotification) {
                            toast.success('General information saved successfully');
                        }
                        return true;
                    } else {
                        if (showNotification) {
                            toast.error('Failed to save general information');
                        }
                        return false;
                    }
                }
            } catch (error) {
                const backendErrors = error.response?.data?.errors;
                if (backendErrors && typeof backendErrors === 'object') {
                    Object.values(backendErrors)
                        .flat()
                        .forEach(msg => toast.error(msg));
                } else {
                    const msg = error.response?.data?.message || 'Failed to save personal information';
                    toast.error(msg);
                }
                return false;      
            }
        };

            // Save employee salary information
        const saveEmployeeSalaryInfo = async (showNotification = true) => {
            try {
                const currentEmployeeId = employeeId.value;

                if (!currentEmployeeId) {
                    toast.error('Employee ID is required');
                    return false;
                }

                // Validate salary form
                const salaryFormValid = await salaryInfoRef.value?.validateAll();
                if (!salaryFormValid) {
                    toast.error('Please fix validation errors in salary information');
                    return false;
                }

                // Prepare salary data
                const salaryData = {
                    salary_info_id: salaryInfo.value.salary_info_id || null, // Include ID for updates
                    employee_id: currentEmployeeId,
                    effect_date: salaryInfo.value.effect_date,
                    package: salaryInfo.value.package,
                    gross_salary: salaryInfo.value.gross_salary || 0,
                    basic_salary: salaryInfo.value.basic_salary || 0,
                    house_rent: salaryInfo.value.house_rent || 0,
                    mobile: salaryInfo.value.mobile || 0,
                    medical: salaryInfo.value.medical || 0,
                    conveyance: salaryInfo.value.conveyance || 0,
                    education: salaryInfo.value.education || 0,
                    transport: salaryInfo.value.transport || 0,
                    technical: salaryInfo.value.technical || 0,
                    festival_allowance: salaryInfo.value.festival_allowance || 0,
                    tiffin: salaryInfo.value.tiffin || 0,
                    incentive: salaryInfo.value.incentive || 0,
                    wash: salaryInfo.value.wash || 0,
                    others: salaryInfo.value.others || 0,
                    tax_deducted: salaryInfo.value.tax_deducted || 0,
                    car_use: salaryInfo.value.car_use || 0,
                    pf_provident_fund: salaryInfo.value.pf_provident_fund || 0,
                    gas_bill: salaryInfo.value.gas_bill || 0,
                    electricity_bill: salaryInfo.value.electricity_bill || 0,
                    telephone_bill: salaryInfo.value.telephone_bill || 0,
                    insurance: salaryInfo.value.insurance || 0,
                    others_deducted: salaryInfo.value.others_deducted || ''
                };

                // Save salary information
                await store.dispatch('saveSalaryInfo', salaryData);

                if (showNotification) {
                    toast.success('Salary information saved successfully');
                }
                return true;
            } catch (error) {
                const backendErrors = error.response?.data?.errors;
                if (backendErrors && typeof backendErrors === 'object') {
                    Object.values(backendErrors)
                        .flat()
                        .forEach(msg => toast.error(msg));
                } else {
                    const msg = error.response?.data?.message || 'Failed to save personal information';
                    toast.error(msg);
                }
                return false;      
            }
        };

        // Get current tab component ref
        const getCurrentTabRef = () => {
            switch (activeTab.value) {
                case 'personal':
                    return personalInfoRef.value;
                case 'general':
                    return generalInfoRef.value;
                case 'salary':
                    return salaryInfoRef.value;
                case 'settings':
                    return settingsInfoRef.value;
                case 'educational':
                    return educationalInfoRef.value;
                case 'address':
                    return addressInfoRef.value;
                case 'previous-employment':
                    return previousEmploymentRef.value;
                case 'other':
                    return otherInfoRef.value;
                default:
                    return null;
            }
        };

        // Go back to employee list
        const goBack = () => {
            router.push('/hrm/employee-list');
        };

        // Initialize component
        onMounted(async () => {
            initializeStaticOptions();
            // Load employee list if not already loaded
            if (store.getters.getHrmEmployeeList.length === 0) {
                await store.dispatch('getHrmEmployeeList');
            }
            await Promise.all([
                loadEmployeeData(),
                loadEmployeeGeneralInfo(),
                loadEmployeeSalaryInfo(),
                loadEmployeeSettingsInfo(),
                loadDropdownData()
            ]);
        });

        return {
            // State
            activeTab,
            saving,
            employeeName,
            loading,
            employeeId,

            // Data
            personalInfo,
            officialInfo,
            salaryInfo,
            settingsInfo,
            // educationalInfo removed - now handled by EducationalInformationNew component
            addressInfo,
            previousEmployment,
            otherInfo,
            bankInfo,
            taxInfo,
            drivingInfo,

            // Dropdown data
            departments,
            sections,
            designations,
            employeeTypes,
            bloodGroups,
            genders,
            religions,
            maritalStatuses,
            banks,
            getHrmEmployeeList,
            loadingStates,
            yearOptions,
            boardOptions,
            examinationOptions,
            resultOptions,

            // Tabs
            tabs,

            // Component refs
            personalInfoRef,
            generalInfoRef,
            salaryInfoRef,
            settingsInfoRef,
            educationalInfoRef,
            addressInfoRef,
            previousEmploymentRef,
            otherInfoRef,

            // Methods
            saveEmployeeDetails,
            saveEmployeeGeneralInfo,
            saveEmployeeSalaryInfo,
            loadEmployeeGeneralInfo,
            loadEmployeeSalaryInfo,
            loadEmployeeSettingsInfo,
            handleTabChange,
            goBack
        };
    }
};
</script>

<style scoped>
.tab-item {
    border-bottom: 1px solid #e0e0e0;
    transition: all 0.3s ease;
}

.tab-item:hover {
    background-color: #f5f5f5;
}

.tab-active {
    background-color: #e3f2fd;
    border-right: 3px solid #2196f3;
}

.border-e {
    border-right: 1px solid #e0e0e0;
}
</style>
